import { useMemo } from "react";

import { useApiQuery } from "@/api";
import { ConsentManagementSlug } from "@/consent-management/consentManagement";
import { SaaSDestination } from "@/destinations/destinations";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { useAllSaasDestinations } from "@/hooks/useAllSaasDestinations";
import { isAccountInHIPAAModeV2 } from "@/util";
import { EnableDestinationContext, EnableDestinationData } from "./types";

export function useEnableDestinationContext({
  destinationSlug,
  destinationConfigId,
}: Pick<
  EnableDestinationData,
  "destinationSlug" | "destinationConfigId"
>): EnableDestinationContext {
  const accountMetadata = useAccountMetadata();
  const allSaasDestinations = useAllSaasDestinations();
  const { data: destinationsData } = useListAllDestinationsForEnv();
  const { data: consentConfigsData } = useApiQuery([
    "get_consent_management_configs",
    {},
  ]);

  const isHipaaV2 =
    accountMetadata.loaded && isAccountInHIPAAModeV2(accountMetadata);
  const destinations = destinationsData?.destinations;
  const consentConfigs = consentConfigsData?.configs;

  return useMemo(() => {
    const candidate = destinationSlug
      ? allSaasDestinations[destinationSlug]
      : undefined;
    const activeConsentManager =
      (Object.entries(consentConfigs ?? {}).find(
        ([, config]) => config.enabled,
      )?.[0] as ConsentManagementSlug | undefined) ?? null;
    const saasDestination =
      candidate instanceof SaaSDestination ? candidate : undefined;

    // The backend doesn't enforce supportsMultiConfig, so a single-config
    // destination must reuse its existing config rather than save a new one.
    const configuredDestination = destinationConfigId
      ? destinations?.find(
          (destination) => destination.id === destinationConfigId,
        )
      : saasDestination && !saasDestination.supportsMultiConfig
        ? destinations?.find(
            (destination) => destination.type_slug === saasDestination.slug,
          )
        : undefined;

    return {
      saasDestination,
      configuredDestination,
      destinationsLoaded: destinations !== undefined,
      isHipaaV2,
      activeConsentManager,
    };
  }, [
    allSaasDestinations,
    consentConfigs,
    destinationConfigId,
    destinationSlug,
    destinations,
    isHipaaV2,
  ]);
}
